import {
  ReactNode,
  ChangeEvent,
  useReducer,
  useState,
  SyntheticEvent,
  useEffect,
} from "react";
import { styled } from "@stitches/react";
import Paginator from "@/components/Paginator";
import LatestNote from "@/components/LatestNote";
import PatientName from "@/components/PatientName";
import LatestNotes from "@/components/LatestNotes";
import ColumnHeader from "@/components/ColumnHeader";
import AuthStatusForm from "@/components/AuthStatusForm";
import { useOrdersContext } from "@/contexts/orders-context";
import {
  Tabs,
  TabPane,
  Table,
  Modal,
  Select,
  MultiselectChangeEvent,
  SingleSelect,
  StructuredOption,
  Input,
  Button,
  Toast,
} from "@/vendor/Forge/dist";
import { AuthStatus, Order, RowData } from "@/types/my-tasks";
import {
  Filter,
  OrdersQueryKey,
  SortOrder,
  Tabs as TabsType,
  ToastMessageType,
} from "@/types/order-table";
import { AUTH_STATUS } from "./constants";
import { useAssignUser, useBucketReAssign } from "@/hooks/use-notes";
import { useAppState } from "@contexts/app-state";
import { capitalize } from "@/utils/helpers";
import Checkbox from "@/vendor/Forge/dist/Checkbox";
import BulkSelectionField from "../BulkSelectionField";
import { useUsers } from "@hooks/use-users";
import PerfDateFilter from "../PerfDateFilter";
import AdvanceFilterContainer from "../AdvanceFilter";
import { useBannerContext } from "@/contexts/banner-context";
import AdvanceFilterWLimitContainer from "../AdvanceFilterWLimit";
import FilterShower from "../FilterShower";

const authStatusMapping: Record<string, AuthStatus> = {
  NOT_STARTED: "Not Started",
  PENDING: "Pending",
  NOT_REQUIRED: "Not Required",
  OBTAINED: "Obtained",
  CLOSED: "Closed",
};

const Wrapper = styled("div", {
  overflow: "auto",
  position: "relative",

  "& ul.fe_c_tabs__labels": {
    border: "none",
  },
  "& .fe_c_tabs__shadow": {
    boxShadow: "none",
  },
  "& .fe_c_table--medium thead th": {
    padding: "16px 12px",
  },
  "& .fe_c_table--medium tbody td.fe_c_table__cell": {
    padding: "8px 12px",
  },
  "& table": {
    borderCollapse: "collapse",
    border: "1px solid #B6B7B7",
    borderLeft: "none",
    width: "100%",
  },
  "& table th.fe_c_table__header--no-wrap": {
    maxWidth: "none",
  },
  "& table th, & table td": {
    border: "1px solid #B6B7B7",
  },
  "& .fe_c_table.fe_c_table--compact tbody td.fe_c_table__cell": {
    padding: "8px 12px",
  },
  "& .fe_c_table.fe_c_table--compact thead th.fe_c_table__header": {
    padding: "8px 12px",
  },
  "& table.fe_c_table.fe_c_table--show-hover.fe_c_table--compact th:first-child, & table td.fe_c_table__cell:first-child":
    {
      borderLeft: "none",
      paddingRight: 0,
    },
});

const PaginatorWrapper = styled("div", {
  position: "absolute",
  top: 0,
  right: 0,
  zIndex: 1,
});

const OrderNameWrapper = styled("div", {
  position: "relative",
});

const PriorityIndicator = styled("div", {
  position: "absolute",
  top: 0,
  left: -18,
  bottom: 0,
  width: 6,
  backgroundColor: "#CA0D0D",
  borderRight: "1px solid #5D5E5E",
});

const AssignWrapper = styled("div", {
  minWidth: 130,
  variants: {
    assigned: {
      false: {
        borderLeft: "5px solid #f3a81c",
        borderRadius: "3px 0 0 3px",
        // overriding the default styles for the SingleSelect component to make the indicator more natural looking
        "& .fe_c_multiselect__control.fe-c-13cymwt-control": {
          borderRadius: "0 3px 3px 0",
        },
      },
    },
  },
});

const SearchWrapper = styled("form", {
  width: 250,
  display: "flex",

  "& .fe_c_input .fe_c_input__input": {
    borderRadius: "3px 0 0 3px",
  },

  "& .fe_c_button": {
    borderRadius: "0 3px 3px 0",
  },
});

const SearchInteractionsContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: 10,
});

interface ModalType {
  show: boolean;
  useLarge: boolean;
  headerText: string;
  renderForm: () => React.ReactNode;
}

const renderPatientName = (name: string, { rowData }: RowData<Order>) => (
  <PatientName
    name={name}
    mrn={String(rowData.patientId)}
    containsMultipleOrder={Boolean(rowData.containMultipleOrder)}
  />
);

const renderLatestNote = (handleLatestNoteClick: (order: Order) => void) =>
  function LatestNotes(_: string, { rowData }: RowData<Order>) {
    return <LatestNote onIconClick={() => handleLatestNoteClick(rowData)} />;
  };

const renderAuthStatus = (
  handleAuthStatusChange: (newStatus: AuthStatus, order: Order) => void,
) =>
  function AuthStatusDropdown(_: string, { rowData }: RowData<Order>) {
    return (
      <Select
        options={AUTH_STATUS}
        hasNullDefault={false}
        defaultValue={authStatusMapping[rowData.authStatus]}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          handleAuthStatusChange(e.target.value as AuthStatus, rowData)
        }
      />
    );
  };

const getSortOrder = (
  sortColumn: string,
  currentSortColumn?: string,
  sortOrder?: SortOrder,
) => (currentSortColumn !== sortColumn ? null : sortOrder);

const getNewSortOrder = (
  sortColumn: string,
  currentSortColumn?: string,
  sortOrder?: SortOrder,
) => {
  if (currentSortColumn !== sortColumn) return "ASC";
  return sortOrder === "DESC" || !sortOrder ? "ASC" : "DESC";
};

interface Props {
  totalItems: number;
  rows: Order[];
  tabs: TabsType[];
  excludedColumns?: string[];
  ordersQueryKey: OrdersQueryKey;
  totalPages: number;
}

const OrderTable = ({
  totalItems,
  rows,
  tabs,
  excludedColumns = [],
  ordersQueryKey,
  totalPages,
}: Props) => {
  const {
    filters,
    pageNumber,
    setPageNumber,
    updateFilters,
    sortOrder,
    sortBy,
    setSortOrder,
    setSortBy,
  } = useOrdersContext();

  const { assignUser } = useAssignUser({ ordersQueryKey });
  const { bucketReAssign } = useBucketReAssign({ ordersQueryKey });
  const { callStatus, updateCallStatus } = useBannerContext();
  const [modal, setModal] = useReducer(
    (state: ModalType, changes: Partial<ModalType>) => ({
      ...state,
      ...changes,
    }),
    {
      show: false,
      useLarge: false,
      headerText: "",
      renderForm: () => null,
    },
  );
  const { loggedInUser } = useAppState();
  const [patientName, setPatientName] = useState<string>("");
  const [checkedOrders, setCheckedOrders] = useState<string[]>([]);
  const [rowState, setRowState] = useState(rows);
  const [checkAll, setCheckAll] = useState(false);
  const [toastMessageData, setToastMessageData] = useState<ToastMessageType>();
  const { data: users = [] } = useUsers();
  const bucketsNoDupe = Array.from(
    new Set([...users.filter((user) => user !== "All")]),
  );

  const handleCheckOrder = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isSelected = e.target.checked;
    const value = e.target.value;

    if (isSelected) {
      setCheckedOrders((prevChecked) => [...prevChecked, value]);
    } else {
      setCheckedOrders((prevData) => {
        return prevData.filter((docId) => {
          return docId !== value;
        });
      });
    }
  };

  useEffect(() => {
    updateCallStatus({
      isAssignUserSucc: false,
      isAuthStatusChangeSucc: false,
      isUpdateBucketSucc: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location]);

  useEffect(() => {
    const documentIds = rows?.map((order) => {
      return order.documentId;
    });

    setRowState(
      rowState?.map((row) => {
        if (checkedOrders.includes(row.documentId)) {
          return { ...row, checked: true };
        } else {
          return { ...row, checked: false };
        }
      }),
    );

    if (documentIds?.length != checkedOrders?.length) {
      setCheckAll(false);
    } else if (documentIds?.length == checkedOrders?.length) {
      setCheckAll(true);
    }
    // eslint-disable-next-line
  }, [checkedOrders]);

  //updates rowstate with cache and clears checks
  useEffect(() => {
    setRowState(rows);
    setCheckedOrders([]);
    setCheckAll(false);
  }, [rows]);

  const handleCheckAll = () => {
    const documentIds = rows?.map((order) => {
      return order.documentId;
    });

    setCheckedOrders(documentIds);

    setCheckAll(!checkAll);

    if (checkedOrders?.length === documentIds?.length) {
      setCheckedOrders([]);
    }
  };

  const deselectAllOrders = (deselectAll: boolean) => {
    if (deselectAll == true) {
      setCheckedOrders([]);
      deselectAll = false;
    }
  };

  const handleLatestNoteClick = (order: Order) => {
    setModal({
      show: true,
      useLarge: true,
      headerText: order.documentType,
      renderForm: () => <LatestNotes order={order} />,
    });
  };

  const handleTabChange = (
    e:
      | ChangeEvent<HTMLSelectElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>,
    selectedTab: number,
  ) => {
    const authStatuses =
      tabs[selectedTab].tabLabel === "Completed"
        ? ["Completed", "Closed", "Not Required", "Obtained"]
        : [tabs[selectedTab].tabLabel];

    updateFilters("authStatus", authStatuses);
    setPageNumber(1);
  };

  const handlePaginationChange = (newPageNumber: number) => {
    setPageNumber(newPageNumber + 1);
  };

  const handleAssigneeListChange = (newAssignee: string, order: Order) => {
    assignUser({
      orderIds: [order.documentId],
      actionNote: `${capitalize(
        loggedInUser,
      )} assigned order to ${newAssignee}.`,
      changedField: "USER_RE_ASSIGN",
      changedValue: newAssignee,
    });
  };
  const handleBucketChange = (newBucket: string, order: Order) => {
    bucketReAssign({
      orderIds: [order.documentId],
      actionNote: `${capitalize(
        loggedInUser,
      )} assigned bucket to ${newBucket}.`,
      changedField: "BUCKET_RE_ASSIGN",
      changedValue: newBucket,
    });
  };

  const handleAuthStatusChange = (newStatus: AuthStatus, order: Order) => {
    if (newStatus.toString() === "") return;
    setModal({
      show: true,
      useLarge: false,
      headerText: `Auth Status → ${newStatus}`,
      renderForm: () => (
        <AuthStatusForm
          ordersQueryKey={ordersQueryKey}
          order={order}
          documentIds={[order.documentId]}
          newStatus={newStatus}
          onSubmit={() => setModal({ show: false })}
          onCancelClick={() => setModal({ show: false })}
        />
      ),
    });
  };

  const handleFilterClick = ({
    type,
    header,
  }: {
    type: Filter;
    header: string;
  }) => {
    setModal({
      show: true,
      useLarge: true,
      headerText: `Filter by ${header}`,
      renderForm: () => (
        <AdvanceFilterWLimitContainer
          type={type}
          useContext={useOrdersContext}
        />
      ),
    });
  };

  const handleOrderStatusFilterClick = ({
    type,
    header,
  }: {
    type: Filter;
    header: string;
  }) => {
    setModal({
      show: true,
      useLarge: true,
      headerText: `Filter by ${header}`,
      renderForm: () => (
        <AdvanceFilterContainer type={type} useContext={useOrdersContext} />
      ),
    });
  };

  const handleFilterPerfDateClick = ({ header }: { header: string }) => {
    setModal({
      show: true,
      useLarge: true,
      headerText: `Filter ${header}`,
      renderForm: () => <PerfDateFilter />,
    });
  };

  const handlePatientNameSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!patientName) return;
    updateFilters(
      "patientNames",
      filters?.patientNames
        ? [...filters.patientNames, patientName]
        : [patientName],
    );
  };

  const handleUrgentFilterClick = () => {
    updateFilters("priority", filters?.priority?.length ? [] : ["true"]);
  };

  const columns = [
    {
      key: "checkbox",
      displayName: "",
      header: {
        formatters: [
          () => (
            <Checkbox
              id="checkboxHeader"
              description=""
              checked={checkAll}
              onChange={handleCheckAll}
            />
          ),
        ],
      },
      template: (text: string, { rowData }: RowData<Order>) => (
        <Checkbox
          key={rowData.documentId}
          id={rowData.documentId}
          value={rowData.documentId}
          description=""
          checked={rowData?.checked}
          onChange={handleCheckOrder}
        />
      ),
    },
    {
      key: "documentType",
      displayName: "Order Name",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="name"
              name={name}
              sortOrder={getSortOrder("orderName", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("orderName", sortBy, sortOrder));
                setSortBy("orderName");
              }}
              onFilterClick={() =>
                handleFilterClick({ type: "orderName", header: "Order Name" })
              }
              isActiveFilter={!!filters?.orderNames?.length}
              activeFilterCount={filters?.orderNames?.length}
            />
          ),
        ],
      },
      template: (text: string, { rowData }: RowData<Order>) => (
        <OrderNameWrapper>
          {rowData.priority && <PriorityIndicator />}
          {text}
        </OrderNameWrapper>
      ),
    },
    {
      key: "documentId",
      displayName: "Document ID",
    },
    {
      key: "patientName",
      displayName: "Patient",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="patient"
              name={name}
              sortOrder={getSortOrder("patientName", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("patientName", sortBy, sortOrder));
                setSortBy("patientName");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "patientName",
                  header: "Patient",
                })
              }
              isActiveFilter={!!filters?.patientNames?.length}
              activeFilterCount={filters?.patientNames?.length}
            />
          ),
        ],
      },
      template: renderPatientName,
    },
    {
      key: "dob",
      displayName: "DOB",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="dob"
              name={name}
              sortOrder={getSortOrder("dob", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("dob", sortBy, sortOrder));
                setSortBy("dob");
              }}
            />
          ),
        ],
      },
    },
    {
      key: "insurance",
      displayName: "Primary",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="insurance"
              name={name}
              sortOrder={getSortOrder("insurance", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("insurance", sortBy, sortOrder));
                setSortBy("insurance");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "insurance",
                  header: "Primary Insurance",
                })
              }
              isActiveFilter={!!filters?.insurances?.length}
              activeFilterCount={filters?.insurances?.length}
            />
          ),
        ],
      },
    },
    {
      key: "secondaryInsurance",
      displayName: "Secondary",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="secondary"
              name={name}
              sortOrder={getSortOrder("secondaryInsurance", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("secondaryInsurance", sortBy, sortOrder),
                );
                setSortBy("secondaryInsurance");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "secondaryInsurance",
                  header: "Secondary Insurance",
                })
              }
              isActiveFilter={!!filters?.secondaryInsurances?.length}
              activeFilterCount={filters?.secondaryInsurances?.length}
            />
          ),
        ],
      },
    },
    {
      key: "clinicalFacilityDetails",
      displayName: "Rad. Facility",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="radFacility"
              name={name}
              sortOrder={getSortOrder(
                "clinicalFacilityDetails",
                sortBy,
                sortOrder,
              )}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("clinicalFacilityDetails", sortBy, sortOrder),
                );
                setSortBy("clinicalFacilityDetails");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "clinicalFacilityDetails",
                  header: "Rad Facility",
                })
              }
              isActiveFilter={!!filters?.clinicalFacilityDetails?.length}
              activeFilterCount={filters?.clinicalFacilityDetails?.length}
            />
          ),
        ],
      },
    },
    {
      key: "orderingProvider",
      displayName: "Ord. Provider",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="provider"
              name={name}
              sortOrder={getSortOrder("orderingProvider", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("orderingProvider", sortBy, sortOrder),
                );
                setSortBy("orderingProvider");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "provider",
                  header: "Order Provider",
                })
              }
              isActiveFilter={!!filters?.providerNames?.length}
              activeFilterCount={filters?.providerNames?.length}
            />
          ),
        ],
      },
    },
    {
      key: "deptName",
      displayName: "Dept",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="department"
              name={name}
              sortOrder={getSortOrder("deptName", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("deptName", sortBy, sortOrder));
                setSortBy("deptName");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "department",
                  header: "Department Name",
                })
              }
              isActiveFilter={!!filters?.departments?.length}
              activeFilterCount={filters?.departments?.length}
            />
          ),
        ],
      },
    },
    {
      key: "performDate",
      displayName: "Perf. Date",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="performDate"
              name={name}
              sortOrder={getSortOrder("performDate", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("performDate", sortBy, sortOrder));
                setSortBy("performDate");
              }}
              onFilterClick={() =>
                handleFilterPerfDateClick({
                  header: "Perf. Date",
                })
              }
              isActiveFilter={
                filters.performDateRangeStar
                  ? filters.performDateRangeStar[0] != ""
                  : false
              }
            />
          ),
        ],
      },
    },
    {
      key: "authStatus",
      displayName: "Auth Status",
      template: renderAuthStatus(handleAuthStatusChange),
    },
    {
      key: "status",
      displayName: "Order Status",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="status"
              name={name}
              sortOrder={getSortOrder("status", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("status", sortBy, sortOrder));
                setSortBy("status");
              }}
              onFilterClick={() =>
                handleOrderStatusFilterClick({
                  type: "status",
                  header: "Status",
                })
              }
              isActiveFilter={!!filters?.statusList?.length}
              activeFilterCount={filters?.statusList?.length}
            />
          ),
        ],
      },
    },
    {
      key: "taoBucket",
      displayName: "Bucket",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="taoBucket"
              name={name}
              sortOrder={getSortOrder("taoBucket", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("taoBucket", sortBy, sortOrder));
                setSortBy("taoBucket");
              }}
            />
          ),
        ],
      },
      template: (selected: string, { rowData }: RowData<Order>) => (
        <Select
          options={bucketsNoDupe}
          hasNullDefault={false}
          value={rowData.taoBucket}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            const selected = e.target.value;
            handleBucketChange(selected, rowData);
          }}
        />
      ),
    },
    {
      key: "assignedUser",
      displayName: "Assign",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="assignedUser"
              name={name}
              onFilterClick={() =>
                handleFilterClick({ type: "assignedUser", header: "Assign To" })
              }
              sortOrder={getSortOrder("assignedUser", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("assignedUser", sortBy, sortOrder),
                );
                setSortBy("assignedUser");
              }}
              isActiveFilter={!!filters?.assignedUser?.length}
              activeFilterCount={filters?.assignedUser?.length}
            />
          ),
        ],
      },
      template: (selected: string, { rowData }: RowData<Order>) => (
        <AssignWrapper assigned={!!rowData.assignedUser}>
          <SingleSelect
            inputId={`assigned-user-select ${rowData.documentId}`}
            options={rowData.allUsers?.map((a) => a.userName)}
            value={rowData.assignedUser ?? "Unassigned"}
            onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
              const selected = e.target.value;
              handleAssigneeListChange(
                selected?.value ?? "Unassigned",
                rowData,
              );
            }}
          />
        </AssignWrapper>
      ),
    },
    {
      key: "latestNote",
      displayName: "Notes",
      template: renderLatestNote(handleLatestNoteClick),
    },
    {
      key: "lastUpdated",
      displayName: "Updated",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="lastUpdated"
              name={name}
              sortOrder={getSortOrder("lastUpdated", sortBy, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("lastUpdated", sortBy, sortOrder));
                setSortBy("lastUpdated");
              }}
            />
          ),
        ],
      },
    },
  ].filter((column) => !excludedColumns.includes(column.key));
  const hasData = Object.entries(filters).some(
    ([key, value]) =>
      !["authStatus", "performDateRangeEn"].includes(key) &&
      Array.isArray(value) &&
      value.some((v) => v !== undefined && v !== null && v !== ""),
  );

  return (
    <Wrapper>
      <PaginatorWrapper>
        <Paginator
          totalPages={totalPages}
          currentPage={pageNumber}
          totalItems={totalItems}
          onPaginationChange={handlePaginationChange}
        />
      </PaginatorWrapper>
      <Tabs
        selectedIndex={tabs?.findIndex(
          (tab) => tab.tabLabel === filters?.authStatus?.[0],
        )}
        onTabsChange={handleTabChange}
      >
        {tabs?.map((task) => (
          <TabPane
            key={task.tabLabel}
            label={`${task.tabLabel} (${task.count})`}
          >
            {checkedOrders?.length > 0 ? (
              <BulkSelectionField
                checkedOrderIds={checkedOrders}
                rowState={rowState}
                deselectAllOrders={deselectAllOrders}
                ordersQueryKey={ordersQueryKey}
                setToastMessageData={setToastMessageData}
              />
            ) : (
              <>
                <SearchInteractionsContainer>
                  <Button
                    icon={filters?.priority?.length ? "Close" : "Critical"}
                    data-testid="test-filter-priority"
                    text="Stat Orders"
                    variant={
                      filters?.priority?.length ? "secondary" : "primary"
                    }
                    onClick={handleUrgentFilterClick}
                  />
                  <SearchWrapper onSubmit={handlePatientNameSubmit}>
                    <Input
                      placeholder="Patient Name"
                      type="text"
                      name="patientName"
                      value={patientName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setPatientName(e.target.value)
                      }
                      data-testid="test-filter-patientName"
                    />
                    <Button
                      icon="Search"
                      type="submit"
                      data-testid="test-filter-patientName-submit"
                    />
                  </SearchWrapper>
                </SearchInteractionsContainer>
                {hasData && <FilterShower filters={filters} />}
              </>
            )}
            <Table
              rowKey="documentId"
              nowrapHeaders
              layout="compact"
              columns={columns}
              rows={rowState}
            />
          </TabPane>
        ))}
      </Tabs>
      <Modal
        show={modal.show}
        headerText={modal.headerText}
        includeButtonRow={false}
        onHide={() => setModal({ show: false })}
        width={modal.useLarge ? "large" : "small"}
      >
        {modal.renderForm()}
      </Modal>
      {callStatus.isAssignUserSucc ? (
        <Toast
          id="reassignBanner"
          alertType="success"
          onDismiss={() => {
            setToastMessageData(undefined);
            updateCallStatus({
              isAssignUserSucc: false,
            });
          }}
          headerText="Success!"
        >
          {toastMessageData?.changedAmount} orders assigned to user{" "}
          {toastMessageData?.changedValue}
        </Toast>
      ) : (
        <></>
      )}
      {callStatus.isAuthStatusChangeSucc ? (
        <Toast
          id="reassignBanner"
          alertType="success"
          onDismiss={() => {
            setToastMessageData(undefined);
            updateCallStatus({
              isAuthStatusChangeSucc: false,
            });
          }}
          headerText="Success!"
        >
          {toastMessageData?.changedAmount} orders Auth Status assigned to{" "}
          {toastMessageData?.changedValue}
        </Toast>
      ) : (
        <></>
      )}
      {callStatus.isUpdateBucketSucc ? (
        <Toast
          id="reassignBanner"
          alertType="success"
          onDismiss={() => {
            setToastMessageData(undefined);
            updateCallStatus({
              isUpdateBucketSucc: false,
            });
          }}
          headerText="Success!"
        >
          {toastMessageData?.changedAmount} orders assigned to Bucket{" "}
          {toastMessageData?.changedValue}
        </Toast>
      ) : (
        <></>
      )}
    </Wrapper>
  );
};

export default OrderTable;
